// frontend/src/components/ErrorBoundary.tsx
import { Component, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[var(--background)] flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-[var(--card)] rounded-lg border border-[var(--border)] shadow-lg p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-[var(--color-brick-bg)] flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-7 h-7 text-[var(--color-brick)]" />
            </div>
            <h2 className="text-lg font-bold text-[var(--foreground)] mb-2">
              Une erreur inattendue est survenue
            </h2>
            <p className="text-sm text-[var(--muted-foreground)] mb-4">
              {this.state.error?.message || "L'application a rencontré un problème."}
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleReset}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--color-steel)] text-white hover:bg-[var(--color-steel-dark)] transition-colors text-sm font-medium"
              >
                <RefreshCw className="w-4 h-4" />
                Réessayer
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="px-4 py-2 rounded-lg bg-[var(--muted)] border border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--border)] transition-colors text-sm font-medium"
              >
                Retour à l'accueil
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
